import { TableColumn, TableStyles } from 'react-data-table-component';
import { WaSalesOfficeSummaryResponse } from 'src/api/wa/store/models/workplace/WaSummary';

export interface BusinessSituation {
  businessLocation: string;
  section: string;
  maintainedUnits: string;
  currentServicedUnits: string;
  withSeismometers: string;
  monitoringandAlarmUnits: string;
  collectedAlarm: string;
  collectionFailed: string;
  seismographOperation: string;
  requiringVerification: string;
  unitsConfirmed: string;
  seismographOpScope: string;
  occurrenceCount: string;
  rescueCount: string;
  rescueRate: string;
  stoppedAndInOperation: string;
  restoredFromShut: string;
  stopRecoveryRate: string;
  temporaryRecoveryUnits: string;
  fullyRestored: string;
  restorationRate: string;
  stoppedUnits: string;
  withPhysicalDamage: string;
  powerOutage: string;
  onCallResponse: string;
  toBeStopped: string;
}

const NUMERIC_COLUMN_WIDTH = '4.5rem';

export const mapSingleItemToBusinessSituation = (
  item: WaSalesOfficeSummaryResponse
): BusinessSituation => {
  const fmt = (val?: number | null) => val?.toLocaleString() ?? '0';
  const pct = (val?: number | null) => `${val?.toFixed(2) ?? '0.00'}%`;

  return {
    businessLocation: item.salesOfficeName || '',
    section: item.kakariName || '',
    maintainedUnits: fmt(item.hosyuCnt),
    currentServicedUnits: fmt(item.hosyuCnt),
    withSeismometers: fmt(item.jishinkeiCnt),
    monitoringandAlarmUnits: fmt(item.jidouTaisyouCnt),
    collectedAlarm: fmt(item.herSumiCnt),
    collectionFailed: fmt(item.sssippaiCnt),
    seismographOperation: fmt(item.teishiDaisu1),
    requiringVerification: fmt(item.taishougaiCnt),
    unitsConfirmed: fmt(item.taishougaiKakuninCnt),
    seismographOpScope: fmt(item.teishiDaisu2),
    occurrenceCount: fmt(item.totalTojikomeCnt),
    rescueCount: fmt(item.tojikomeKyuushustuCnt),
    rescueRate: pct(item.tojikomeKyuushustuRate),
    stoppedAndInOperation: fmt(item.teishiCnt),
    restoredFromShut: fmt(item.fukkyuDaisu),
    stopRecoveryRate: pct(item.fukkyuRate),
    temporaryRecoveryUnits: fmt(item.hdKariFukkyuuCnt),
    fullyRestored: fmt(item.hdHonFukkyuuCnt),
    restorationRate: pct(item.hdHonFukkyuuRate),
    stoppedUnits: fmt(item.yousyoriCnt),
    withPhysicalDamage: fmt(item.bussonCnt),
    powerOutage: fmt(item.teidenCnt),
    onCallResponse: fmt(item.oncallTaiouDaisu),
    toBeStopped: fmt(item.grTeishiYosokuCnt),
  };
};

export const businessSummaryCustomStyles: TableStyles = {
  tableWrapper: {
    style: {
      display: 'block',
      overflow: 'visible',
    },
  },
  table: {
    style: {
      backgroundColor: 'transparent',
      width: '100%',
      tableLayout: 'fixed',
    },
  },
  rows: {
    style: {
      minHeight: '2.25rem',
      borderBottom: '1px solid #d1d5db',
    },
  },
  cells: {
    style: {
      borderRight: '1px solid #d1d5db',
      padding: '0px',
      fontSize: '0.75rem',
    },
  },
};

const rightCol = (
  selector: (row: BusinessSituation) => string,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<BusinessSituation> => ({
  cell: (row) => {
    const isTotal = row.section === '';
    return (
      <div className={`flex w-full items-center justify-end pr-2 ${isTotal ? 'my-4' : ''}`}>
        {selector(row)}
      </div>
    );
  },
  width,
});

export const businessSummaryColumns = (): TableColumn<BusinessSituation>[] => [
  {
    name: 'Business Location',
    width: NUMERIC_COLUMN_WIDTH,
    cell: (row) => <div className="w-full text-center font-bold">{row.businessLocation}</div>,
  },
  {
    width: NUMERIC_COLUMN_WIDTH,
    cell: (row) => <div className="w-full text-center font-bold">{row.section}</div>,
  },
  rightCol((row) => row.currentServicedUnits),
  rightCol((row) => row.withSeismometers),
  rightCol((row) => row.monitoringandAlarmUnits),
  rightCol((row) => row.collectedAlarm),
  rightCol((row) => row.collectionFailed),
  rightCol((row) => row.seismographOperation),
  rightCol((row) => row.requiringVerification),
  rightCol((row) => row.unitsConfirmed),
  rightCol((row) => row.seismographOpScope),
  rightCol((row) => row.occurrenceCount),
  rightCol((row) => row.rescueCount),
  rightCol((row) => row.rescueRate),
  rightCol((row) => row.stoppedAndInOperation),
  rightCol((row) => row.restoredFromShut),
  rightCol((row) => row.stopRecoveryRate),
  rightCol((row) => row.temporaryRecoveryUnits),
  rightCol((row) => row.fullyRestored),
  rightCol((row) => row.restorationRate),
  rightCol((row) => row.stoppedUnits),
  rightCol((row) => row.withPhysicalDamage),
  rightCol((row) => row.powerOutage),
  rightCol((row) => row.onCallResponse),
  rightCol((row) => row.toBeStopped),
];
